// What a LIVE run preview replaces on the 2D floor plan.
//
// While a run gesture is in flight — an appliance drop, a run-end drag, a bay
// drag — the run re-solves into a ghost layer each frame and nothing is written
// to the room store until mouseup (`useRunDrag`). Every overlay drawn during
// that window therefore has to read the PREVIEW, not the room: the room still
// holds the pre-gesture positions.

/**
 * Is this stored object superseded by the preview — the run's own objects, plus
 * anything the preview names in `hideIds` (a dropped appliance on an auto run
 * carries no `runId`)?
 */
export const supersededByRunPreview = (preview, o) =>
  o?.runId === preview?.runId || !!preview?.hideIds?.has(o?.id);

/**
 * The subject and the object list a drag overlay should be drawn from.
 *
 * With no preview these are just what the caller already had. With one, the
 * subject becomes its previewed copy and the superseded objects are swapped for
 * the preview's — so an appliance's clearance zone travels with the ghost
 * instead of staying where the appliance used to be, and the repacked
 * neighbours' zones move with theirs.
 *
 * @param {Object} selected the selected object (the room's copy)
 * @param {Object[]} roomObjects the room's stored objects
 * @param {Object|null} preview `{ runId, hideIds, objects }` or null
 * @returns {{subject: Object, objects: Object[]}}
 */
export function overlayObjectsUnderPreview(selected, roomObjects, preview) {
  const objects = roomObjects || [];
  const previewed = preview?.objects?.length ? preview.objects : null;
  if (!previewed) return { subject: selected, objects };
  return {
    // A run gesture that does not move the selected object itself (a run-end
    // drag with a cabinet selected) leaves it as it was.
    subject: previewed.find((o) => o?.id === selected?.id) || selected,
    objects: [
      ...objects.filter((o) => !supersededByRunPreview(preview, o)),
      ...previewed,
    ],
  };
}
